import React, { useContext, useEffect, useState, useRef } from "react";
import { FiRefreshCw } from "react-icons/fi";
import { Button as ButtonLib, ColorPicker } from "@npm_leadtech/qr-ui-components";
import { QREditorContext } from "@/contexts/QREditorContext";
import { QRProps } from "@/contexts/QREditorContext/types";
import { checkValidHexColor } from "@/utils/formHelper";
import { ColorGroupControlledProps } from "./types";
import { useFormContext } from "react-hook-form";
import { useDebounce } from "@/hooks/utils/useDebounce";
import "./styles.scss";

export const ColorGroupControlled = (props: ColorGroupControlledProps) => {
  const {
    primaryColor: {
      id: primaryColorId,
      label: primaryColorLabel,
      color: primaryColor,
      errorMessage: generalErrorMessage,
    },
    secondaryColor: { id: secondaryColorId, label: secondaryColorLabel, color: secondaryColor },
    className,
    onChange,
  } = props;

  const qrEditorContext = useContext(QREditorContext);
  const { clearErrors, setError, setValue, formState } = useFormContext();
  const { errors } = formState;

  const lastContextUpdate = useRef<{ first: string | null; second: string | null }>({
    first: qrEditorContext?.qrData?.qrStyle?.[primaryColorId] || null,
    second: qrEditorContext?.qrData?.qrStyle?.[secondaryColorId] || null,
  });

  // Flag to avoid that the debounce useEffect overwrites the values after the switch
  const skipDebounceUpdate = useRef(false);

  const [firstColor, setFirstColor] = useState(
    qrEditorContext?.qrData?.qrStyle?.[primaryColorId] || primaryColor
  );
  const [secondColor, setSecondColor] = useState(
    qrEditorContext?.qrData?.qrStyle?.[secondaryColorId] || secondaryColor
  );

  const debouncedFirstColor = useDebounce(firstColor, 50);
  const debouncedSecondColor = useDebounce(secondColor, 50);
  const getErrorMessageByRule = (rule: string): string => {
    if (!generalErrorMessage || !Array.isArray(generalErrorMessage)) {
      return "";
    }
    const error = generalErrorMessage.find((err) => err.rule === rule);
    return error ? error.message : "";
  };
  const isValidColor = (color: string, fieldName: string): boolean => {
    const isValid = checkValidHexColor(color);

    if (!color) {
      setError(fieldName, {
        type: "required",
        message: getErrorMessageByRule("required"),
      });
      return false;
    }

    if (!isValid) {
      setError(fieldName, {
        type: "pattern",
        message: getErrorMessageByRule("pattern"),
      });
      return false;
    }

    return true;
  };

  const handleSwitchColors = () => {
    if (!qrEditorContext) return;

    const isValidFirst = isValidColor(firstColor, primaryColorId);
    const isValidSecond = isValidColor(secondColor, secondaryColorId);

    if (isValidFirst && isValidSecond) {
      clearErrors(primaryColorId);
      clearErrors(secondaryColorId);

      const newFirstColor = secondColor;
      const newSecondColor = firstColor;

      skipDebounceUpdate.current = true;

      setFirstColor(newFirstColor);
      setSecondColor(newSecondColor);

      setValue(primaryColorId, newFirstColor);
      setValue(secondaryColorId, newSecondColor);

      const qrData = qrEditorContext.qrData as QRProps<any>;
      lastContextUpdate.current = { first: newFirstColor, second: newSecondColor };
      qrEditorContext.setQrData({
        ...qrData,
        qrStyle: {
          ...qrData.qrStyle,
          [primaryColorId]: newFirstColor,
          [secondaryColorId]: newSecondColor,
        },
      });

      setTimeout(() => {
        skipDebounceUpdate.current = false;
      }, 350);
    }
  };

  const handleFirstColorChange = (color: string) => {
    setFirstColor(color);
    onChange?.(color, "primary");
  };

  const handleSecondColorChange = (color: string) => {
    setSecondColor(color);
    onChange?.(color, "secondary");
  };

  useEffect(() => {
    const isValid = isValidColor(debouncedFirstColor, primaryColorId);
    setValue(primaryColorId, debouncedFirstColor);

    if (isValid) {
      clearErrors(primaryColorId);
    }
  }, [debouncedFirstColor]);

  useEffect(() => {
    const isValid = isValidColor(debouncedSecondColor, secondaryColorId);
    setValue(secondaryColorId, debouncedSecondColor);

    if (isValid) {
      clearErrors(secondaryColorId);
    }
  }, [debouncedSecondColor]);

  useEffect(() => {
    if (skipDebounceUpdate.current) return;
    if (!qrEditorContext) return;

    const validFirst = checkValidHexColor(debouncedFirstColor);
    const validSecond = checkValidHexColor(debouncedSecondColor);

    if (!validFirst || !validSecond) return;

    const qrData = qrEditorContext.qrData as QRProps<any>;

    if (
      qrData?.qrStyle?.[primaryColorId] === debouncedFirstColor &&
      qrData?.qrStyle?.[secondaryColorId] === debouncedSecondColor
    ) {
      return;
    }

    lastContextUpdate.current = { first: debouncedFirstColor, second: debouncedSecondColor };

    qrEditorContext.setQrData({
      ...qrData,
      qrStyle: {
        ...qrData.qrStyle,
        [primaryColorId]: debouncedFirstColor,
        [secondaryColorId]: debouncedSecondColor,
      },
    });
  }, [debouncedFirstColor, debouncedSecondColor, primaryColorId, secondaryColorId]);

  // Sync local state when external context changes (templates, frames, etc.)
  useEffect(() => {
    const contextFirstColor = qrEditorContext?.qrData?.qrStyle?.[primaryColorId];
    const contextSecondColor = qrEditorContext?.qrData?.qrStyle?.[secondaryColorId];

    if (!contextFirstColor || !contextSecondColor) return;

    const isExternalFirstChange =
      contextFirstColor !== firstColor && contextFirstColor !== lastContextUpdate.current.first;

    const isExternalSecondChange =
      contextSecondColor !== secondColor && contextSecondColor !== lastContextUpdate.current.second;

    if (isExternalFirstChange || isExternalSecondChange) {
      lastContextUpdate.current = {
        first: contextFirstColor,
        second: contextSecondColor,
      };

      if (isExternalFirstChange) {
        setFirstColor(contextFirstColor);
      }
      if (isExternalSecondChange) {
        setSecondColor(contextSecondColor);
      }
    }
  }, [qrEditorContext?.qrData?.qrStyle]);

  return (
    <div
      suppressHydrationWarning
      data-testid="color-group"
      data-qa="color-group"
      className={`color-group ${className || ""}`}
    >
      <ColorPicker
        id={primaryColorId}
        label={primaryColorLabel}
        value={firstColor}
        className="color-picker-input"
        placeholder={primaryColor}
        defaultValue={primaryColor}
        onChange={handleFirstColorChange}
        errorMessage={errors?.[primaryColorId]?.message as string}
        dataQA={`color-group-element-${primaryColorId}`}
        dataTestId={`color-group-element-${primaryColorId}`}
      />

      <ButtonLib
        shape="ghost"
        ariaLabel="Switch colors"
        id="color-group-button"
        dataQA="color-group-button"
        iconLeft={<FiRefreshCw />}
        onClick={handleSwitchColors}
      />

      <ColorPicker
        id={secondaryColorId}
        label={secondaryColorLabel}
        value={secondColor}
        className="color-picker-input"
        placeholder={secondaryColor}
        defaultValue={secondaryColor}
        onChange={handleSecondColorChange}
        errorMessage={errors?.[secondaryColorId]?.message as string}
        dataQA={`color-group-element-${secondaryColorId}`}
        dataTestId={`color-group-element-${secondaryColorId}`}
      />
    </div>
  );
};
